import React, { useEffect, useState } from 'react';
import Loader from '../../common/Loader/Loader';
import GroupListItemCollapse from '../../navigation/icons/GroupListItemCollapse';
import GroupListItemExpand from '../../navigation/icons/GroupListItemExpand';
import NavigationIcons from '../../navigation/components/NavigationIcons';
import GroupIcon from '../../navigation/icons/GroupIcon';
import { useAppSelector, useAppDispatch } from '../../../hooks/storeHooks';
import { setPCSelectedAssets } from '../WellFilterSlice';
import '../WellFilterPanel.scss';

interface AssetItems {
  assetId: string;
  assetName: string;
  industryApplicationId: number;
  wellOpType: number | null;
  enabled: boolean;
}

interface GroupItems {
  id: number;
  assetGroupName: string;
  childGroups: GroupItems[];
  assets: AssetItems[];
  showAssets: boolean;
  class: string;
}

interface WellGroupDropDownProps {
  onGroupSelect?: (groupName: string) => void;
  onSelectedGroupsChange?: (selectedGroupNames: string[]) => void;
  selectedGroups?: string[];
  PCFilter?: boolean;
  onSelectedAssetsChange?: (selectedAssetGuids: string[]) => void;
  selectedAssets?: string[];
}

const WellGroupDropDown: React.FC<WellGroupDropDownProps> = ({
  onGroupSelect,
  onSelectedGroupsChange,
  selectedGroups = [],
  PCFilter = false,
  onSelectedAssetsChange,
  selectedAssets = [],
}) => {
  const dispatch = useAppDispatch();
  const [assetGroups, setAssetGroups] = useState<GroupItems[] | null>(null);
  const [selectedGroupNames, setSelectedGroupNames] = useState<Set<string>>(new Set(selectedGroups));
  const [expandedGroups, setExpandedGroups] = useState<{ [key: number]: boolean }>({});

  const dataGroup = useAppSelector((state) => state.assetGroups?.groups || []);
  const pcSelectedAssetsFromStore = useAppSelector((state) => state.WellFiltersSlice.PCSelectedAssets);
  const selectedAssetGuids = PCFilter ? new Set(pcSelectedAssetsFromStore) : new Set(selectedAssets);

  // Initialize groups with showAssets property
  const initializeGroupsWithDefaults = (groups: any[]): GroupItems[] => {
    const shouldAutoExpand = groups.length === 1;

    return groups.map((group, index) => ({
      ...group,
      showAssets: shouldAutoExpand && index === 0,
      childGroups: group.childGroups
        ? group.childGroups.map((childGroup: any) => ({
            ...childGroup,
            showAssets: false,
            childGroups: childGroup.childGroups || [],
          }))
        : [],
    }));
  };

  useEffect(() => {
    if (dataGroup && dataGroup.length > 0) {
      const initializedGroups = initializeGroupsWithDefaults(dataGroup);
      setAssetGroups(initializedGroups);

      // If there's only one top-level group, auto-expand it to show first 3 items
      if (dataGroup.length === 1) {
        const firstGroup = dataGroup[0];
        const totalItems = (firstGroup.childGroups?.length || 0) + (firstGroup.assets?.length || 0);
        if (totalItems > 3) {
          setExpandedGroups({ [firstGroup.id]: false });
        }
      }
    }
  }, [dataGroup]);

  useEffect(() => {
    setSelectedGroupNames(new Set(selectedGroups));
  }, [selectedGroups]);

  const handleAssetClick = (assetId: string) => {
    if (!PCFilter) return;

    const currentSelected = pcSelectedAssetsFromStore;
    let updatedAssets: string[];

    if (currentSelected.includes(assetId)) {
      updatedAssets = currentSelected.filter((id) => id !== assetId);
    } else {
      updatedAssets = [...currentSelected, assetId];
    }

    dispatch(setPCSelectedAssets(updatedAssets));
    onSelectedAssetsChange?.(updatedAssets);
  };

  const handleAssetGroupClick = (groupName: string) => {
    setSelectedGroupNames((prev) => {
      const updated = new Set(prev);
      updated.has(groupName) ? updated.delete(groupName) : updated.add(groupName);

      onSelectedGroupsChange?.([...updated]);
      onGroupSelect?.(groupName);

      return updated;
    });
  };

  const toggleShowAssets = (groups: GroupItems[], id: number): GroupItems[] => {
    return groups.map((group) => {
      if (group.id === id) {
        return {
          ...group,
          showAssets: !group.showAssets,
        };
      }
      if (group.childGroups && group.childGroups.length > 0) {
        return {
          ...group,
          childGroups: toggleShowAssets(group.childGroups, id),
        };
      }
      return group;
    });
  };

  const handleShowMoreToggle = (groupId: number) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const renderGroup = (group: GroupItems, index: number, indentLevel = 0, isTopLevel = false) => {
    const isGroupSelected = selectedGroupNames.has(group.assetGroupName);

    let childGroupsToShow = group.childGroups;
    let assetsToShow = group.assets;
    let showMoreNeeded = false;
    let showMoreLabel = '';
    if (isTopLevel) {
      const expanded = expandedGroups[group.id];
      const totalItems = group.childGroups.length + group.assets.length;
      showMoreNeeded = totalItems > 3;
      showMoreLabel = expanded ? 'Show less' : 'Show more';

      if (!expanded && showMoreNeeded) {
        const firstThree = [
          ...group.childGroups.slice(0, 3),
          ...group.assets.slice(0, Math.max(0, 3 - group.childGroups.length)),
        ].slice(0, 3);
        childGroupsToShow = firstThree.filter((item): item is GroupItems => 'childGroups' in item);
        assetsToShow = firstThree.filter((item): item is AssetItems => 'assetId' in item);
      }
    }

    return (
      <div key={group.assetGroupName + index.toString()} className='flex flex-col gap-1'>
        <div className={`${group.class} ${!PCFilter && isGroupSelected ? 'item-selected' : ''}`}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '8px',
              width: '100%',
              alignItems: 'center',
              paddingLeft: `${indentLevel * 12}px`,
              cursor: PCFilter ? 'default' : 'pointer',
            }}
            onClick={() => !PCFilter && handleAssetGroupClick(group.assetGroupName)}
          >
            <GroupIcon
              color={!PCFilter && isGroupSelected ? '#86CFE3' : '#fcfcfd'}
              useAssetsIcon={group.assets.length > 0}
            />

            <div className='navigation-content-frame-item-content'>
              <div
                className={`navigation-content-frame-item-text ${!PCFilter && isGroupSelected ? 'text-active' : ''}`}
              >
                {group.assetGroupName}
              </div>
            </div>
            <div className={`navigation-content-frame-item-count ${!PCFilter && isGroupSelected ? 'active' : ''}`}>
              {(group.childGroups?.length ?? 0) + (group.assets?.length ?? 0)}
            </div>
          </div>
          <div
            onClick={(e) => {
              e.stopPropagation();
              setAssetGroups((prev) => (prev ? toggleShowAssets(prev, group.id) : prev));
            }}
            style={{ cursor: 'pointer' }}
          >
            {group.showAssets ? (
              <GroupListItemCollapse isActive={!PCFilter && isGroupSelected} />
            ) : (
              <GroupListItemExpand isActive={!PCFilter && isGroupSelected} />
            )}
          </div>
        </div>
        {group.showAssets && (
          <div className='flex flex-col'>
            {childGroupsToShow?.map((childGroup, childIndex) =>
              renderGroup(childGroup, index + childIndex + 1, indentLevel + 1),
            )}
            {assetsToShow?.map((asset, assetIndex) => {
              const isAssetSelected = PCFilter && selectedAssetGuids.has(asset.assetId);
              return (
                <div
                  key={asset.assetName + assetIndex.toString()}
                  className={`navigation-content-frame-asset ${PCFilter && isAssetSelected ? 'item-selected' : ''}`}
                  onClick={() => PCFilter && handleAssetClick(asset.assetId)}
                  style={{ cursor: PCFilter ? 'pointer' : 'default' }}
                >
                  <div
                    className='navigation-content-frame-item-text '
                    style={{
                      paddingLeft: `${(indentLevel + 1) * 12}px`,
                      color: PCFilter && isAssetSelected ? '#60BFDA' : '',
                    }}
                  >
                    <div
                      className={
                        asset.enabled
                          ? 'navigation-content-well-enable-status enabled'
                          : 'navigation-content-well-enable-status disabled'
                      }
                    />
                    <NavigationIcons asset={asset} isActive={PCFilter && isAssetSelected} />
                    {asset.assetName}
                  </div>
                </div>
              );
            })}
            {isTopLevel && showMoreNeeded && (
              <button
                type='button'
                className='well-filter-show-more'
                style={{ marginLeft: `${(indentLevel + 2) * 12}px` }}
                onClick={() => handleShowMoreToggle(group.id)}
              >
                {showMoreLabel}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className='navigation-bar-items filter-group-bar-items'>
      <div className='navigation-bar-items-content' style={{ paddingTop: '0px' }}>
        {assetGroups == null ? (
          <Loader />
        ) : (
          <div className='navigation-content-frame' style={{ width: '475px' }}>
            {assetGroups.map((group, index) => renderGroup(group, index, 0, true))}
          </div>
        )}
      </div>
    </div>
  );
};
export default WellGroupDropDown;
